import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  Typography,
  IconButton,
  Button,
  Box,
  Chip,
} from '@mui/material';
import { Delete as DeleteIcon, OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import { useFeedback } from '../../contexts/FeedbackContext';
import ConfirmDialog from '../shared/ConfirmDialog';

function formatTimestamp(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      dateStyle: 'short',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}

function FeedbackCommentCard({ comment }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { deleteComment, closePanel } = useFeedback();
  const [deleteOpen, setDeleteOpen] = useState(false);

  const isCurrentScreen = comment.screenPath === location.pathname;

  const handleGoToScreen = () => {
    navigate(comment.screenPath);
    closePanel();
  };

  const handleDeleteClick = () => setDeleteOpen(true);
  const handleDeleteCancel = () => setDeleteOpen(false);
  const handleDeleteConfirm = async () => {
    const deleted = await deleteComment(comment.id);
    if (deleted) setDeleteOpen(false);
  };

  return (
    <>
      <Card
        sx={{
          borderRadius: '8px',
          mb: 1.5,
          backgroundColor: 'background.paper',
          border: '1px solid rgba(11, 32, 56, 0.10)',
          boxShadow: '0 1px 3px rgba(11, 32, 56, 0.06)',
          transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
          '&:hover': {
            borderColor: 'rgba(134, 170, 196, 0.42)',
            boxShadow: '0 8px 18px rgba(11, 32, 56, 0.08)',
          },
        }}
      >
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Typography variant="body1" sx={{ mb: 1, wordBreak: 'break-word' }}>
            {comment.text}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {formatTimestamp(comment.timestamp)}
            </Typography>
            <Chip
              label={comment.screenLabel}
              size="small"
              variant="outlined"
              sx={{
                fontSize: '0.75rem',
                height: 22,
                ...(isCurrentScreen && {
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  fontWeight: 600,
                }),
              }}
            />
            {isCurrentScreen && (
              <Chip
                label={t('feedback.youAreHere')}
                size="small"
                color="primary"
                sx={{ fontSize: '0.7rem', height: 20 }}
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
            <Button
              size="small"
              startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
              onClick={handleGoToScreen}
              sx={{ textTransform: 'none', fontSize: '0.8rem', minWidth: 0, px: 1 }}
            >
              {t('feedback.goToScreen')} ↗
            </Button>
            <IconButton
              size="small"
              onClick={handleDeleteClick}
              sx={{ ml: 'auto', color: 'text.secondary' }}
              aria-label={t('common.delete')}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        </CardContent>
      </Card>
      <ConfirmDialog
        open={deleteOpen}
        title={t('feedback.deleteTitle')}
        message={t('feedback.deleteConfirm')}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        confirmColor="error"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </>
  );
}

export default FeedbackCommentCard;
